<template>
  <div class="site-header-mobile">
    <div class="site-header-mobile__item site-header-mobile__hamburger">
      <button
        class="site-header-mobile__icon site-header-mobile__icon--hamburger"
        :aria-label="toggleButtonText"
        @click="toggleMenu"
      >
        <icon-mobile-close
          v-if="isOpened"
          :aria-label="closeButtonText"
        />
        <icon-hamburger
          v-else
          :aria-label="openButtonText"
        />
      </button>
    </div>
    <div class="site-header-mobile__item site-header-mobile__logo">
      <meta
        itemprop="url"
        :content="rootURL"
      >
      <template v-if="logoObject">
        <a
          :href="rootURL"
          class="logo logo--image"
          :aria-label="shopName"
        >
          <img
            itemprop="logo"
            :src="logoSrc"
            :alt="logoObject.alt || shopName"
          >
        </a>
        <a
          v-if="altLogoObject"
          :href="rootURL"
          class="logo logo--image-alt"
          :aria-label="shopName"
        >
          <img
            itemprop="logo"
            :src="altLogoSrc"
            :alt="altLogoObject.alt || shopName"
          >
        </a>
      </template>
      <a
        v-else
        class="logo logo--text"
        :href="rootURL"
        :aria-label="shopName"
        v-html="logoText || shopName"
      />
    </div>
    <div class="site-header-mobile__item site-header-mobile__icons">
      <button
        v-if="enableHeaderSearch"
        type="button"
        class="site-header-mobile__icon site-header-mobile__icon--search search-app__trigger"
        :aria-label="toggleSearchText"
      >
        <icon-search-mobile />
      </button>

      <cart-button
        v-if="enableHeaderCart"
        class="site-header-mobile__icon site-header-mobile__icon--cart"
      />
    </div>
  </div>
  <div
    v-if="showBreadcrumb"
    class="site-header-mobile-breadcrumb"
  >
    <icon-left />
    <a
      :href="breadcrumb.url"
      :title="breadcrumb.title"
      v-text="breadcrumb.title"
    />
  </div>

  <transition name="slide-right">
    <hamburger-menu
      v-if="isOpened"
      :enable-header-wishlist="enableHeaderWishlist"
      :enable-header-account="enableHeaderAccount"
      :enable-mobile-carousel="enableMobileCarousel"
      :mobile-top-carousel="mobileTopCarousel"
      :enable-link-background="enableLinkBackground"
      :wishlist-url="wishlistUrl"
      :main-menu="mainMenu"
      :section-blocks="sectionBlocks"
    />
  </transition>
</template>

<script>

/**
 * Component: Site Header Mobile
 * ----------------------------------------------------------------------------
 * @param {Object}  [logoObject] - A formatted image logo.
 * @param {Object}  [altLogoObject] - A formatted alternative image logo.
 * @param {String}  [logoSrc] - Logo file src with responsive size (max-width from settings).
 * @param {String}  [altLogoSrc] - Alternative logo src with responsive size.
 * @param {String}  [logoText] - Text (SVG) setting for header logo.
 * @param {String}  [wishlistUrl] - Link to the Wishlist page.
 *
 * @param {Boolean} [enableHeaderAccount] - Setting for enabling header account.
 * @param {Boolean} [enableHeaderCart] - Setting for enabling header cart.
 * @param {Boolean} [enableHeaderSearch] - Setting for enabling header search.
 * @param {Boolean} [enableHeaderWishlist] - Setting for enabling header wishlist.
 * @param {Boolean} [enableMobileCarousel] - Setting for enabling header collection menu slider on Mobile Menu.
 * @param {Boolean} [enableLinkBackground] - Setting for enabling background styling for highlighted links.
 *
 * @param {Object}  [breadcrumb] - An object, that contains collection URL and title for breadcrumb that is shown only on PDP.
 * @param {Array}   [mobileTopCarousel] - Formatted nested linklist with collections links and images inside.
 * @param {Object}  [mainMenu] - Formatted nested linklist.
 * @param {Array}   [sectionBlocks] - Formatted section blocks array.
 * @param {Boolean} [isSearchOpened] - Value which indicates the current state of search overlay.
 */

import { defineAsyncComponent } from 'vue'

import classes from '~/helpers/classes'

import CartButton from '~components/site-header-components/cart-button/cart-button'

import IconHamburger from '~icons/header/icon-hamburger.svg'
import IconMobileClose from '~icons/header/icon-mobile-close.svg'
import IconSearchMobile from '~icons/header/icon-search-mobile.svg'
import IconLeft from '~icons/direction/left.svg'

export default {
  name: 'SiteHeaderMobile',

  components: {
    CartButton,
    IconHamburger,
    IconMobileClose,
    IconSearchMobile,
    IconLeft,
    HamburgerMenu: defineAsyncComponent(() =>
      import('~components/site-header-components/hamburger-menu/hamburger-menu'),
    ),
  },

  props: {
    logoObject: {
      type: [Boolean, Object],
      default: false,
    },
    altLogoObject: {
      type: [Boolean, Object],
      default: false,
    },
    logoSrc: {
      type: String,
      default: '',
    },
    altLogoSrc: {
      type: String,
      default: '',
    },
    logoText: {
      type: String,
      default: '',
    },
    wishlistUrl: {
      type: String,
      default: '',
    },
    enableHeaderCart: {
      type: Boolean,
      default: false,
    },
    enableHeaderAccount: {
      type: Boolean,
      default: false,
    },
    enableHeaderSearch: {
      type: Boolean,
      default: false,
    },
    enableHeaderWishlist: {
      type: Boolean,
      default: false,
    },
    enableMobileCarousel: {
      type: Boolean,
      default: false,
    },
    enableLinkBackground: {
      type: Boolean,
      default: false,
    },
    breadcrumb: {
      type: [Boolean, Object],
      default: false,
    },
    mobileTopCarousel: {
      type: Array,
      default:() => [],
    },
    mainMenu: {
      type: Array,
      default: () => [],
    },
    sectionBlocks: {
      type: Array,
      default: () => [],
    },
    isSearchOpened: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['on-mobile-menu-toggle'],

  data() {
    return {
      isOpened: false,
    }
  },

  computed: {

    /**
     * Calculate shop root URL for announcement message
     * @returns {String}
     */
    rootURL() {
      return mint.routes.root
    },

    /**
     * Calculate liquid `shop name` to display by default if no logo is set
     * @returns {String}
     */
    shopName() {
      return mint.shop.name
    },

    /**
     * Calculate if to show the breadcrumb.
     * - Don't show if there is no breadcrumb link
     * - Don't show on the Gift Card pages
     * @returns {Boolean}
     */
    showBreadcrumb() {
      if (!this.breadcrumb) {
        return false
      }

      return (
        mint.request.pageType === 'product' &&
        mint.product?.type !== 'Gift Cards'
      )
    },

    /**
     * Calculate the 'Close' button text.
     * @returns {String}
     */
    closeButtonText() {
      return mint.strings.accessibility.close
    },

    /**
     * Calculate the 'Open' button text.
     * @returns {String}
     */
    openButtonText() {
      return mint.strings.accessibility.open
    },

    /**
     * Calculate the 'Toggle Menu' button text.
     * @returns {String}
     */
    toggleButtonText() {
      return mint.strings.header.toggleMenu
    },

    /**
     * Calculate the 'Toggle Search' button text.
     * @returns {String}
     */
    toggleSearchText() {
      return mint.strings.header.toggleSearch
    },
  },

  watch: {

    /**
     * Watching the `isSearchOpened` prop. If the value is true and mobile menu
     * is opened as well, then the menu should be closed to show search overlay.
     */
    isSearchOpened: {
      handler: function (newValue, oldValue) {
        if (newValue === true && this.isOpened) this.toggleMenu()
      },
    },
  },

  mounted() {
    this.setEventListeners()
  },

  beforeUnmount() {

    /**
     * Remove the locked class from the body when switching to Desktop
     */
    document.body.classList.remove(classes.locked)
  },

  methods: {

    /**
     * Set event listeners.
     */
    setEventListeners() {

      /**
       * If the cart drawer is opened while the mobile menu is open, close it.
       */
      mint.EventBus.on('cartDrawer:open', () => {
        if (this.isOpened) {
          this.toggleMenu()
        }
      })
    },

    /**
     * Toggle hamburger menu display.
     *
     * @emits `on-mobile-menu-toggle`
     */
    toggleMenu() {
      this.isOpened = !this.isOpened
      this.$emit('on-mobile-menu-toggle', this.isOpened)
    },
  },
}
</script>

<style src="./site-header-mobile.scss" lang="scss"></style>
